import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';

import { ActionIcon, Button, Group, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { openModal } from '@mantine/modals';
import {
  IconAlertCircle,
  IconInfoSquareRoundedFilled,
  IconPlus,
} from '@tabler/icons-react';
import { mapUsersRoles } from '@utils/mappers';

import { User, UserRole } from '@interfaces/auth.interface';

import {
  GetUsersRequestParams,
  GetUsersSortBy,
  useCountUsersQuery,
  useGetUsersQuery,
} from '@api/users.api';

import CreatePasswordlessUserModal from '@components/CreatePasswordlessUserModal';
import { LayoutContextType } from '@components/layout/Layout';
import QSSelect from '@components/QSSelect';
import SearchBar from '@components/SearchBar';

export default function Users() {
  // ==========================================================================
  // State
  // ==========================================================================
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState<GetUsersRequestParams>({
    page: +(searchParams.get('page') || 1),
    pageLength: +(searchParams.get('pageLength') || 50),
    searchQuery: searchParams.get('search') || undefined,
    role: searchParams.get('role')
      ? (searchParams.get('role') as UserRole)
      : undefined,
    sortBy: 'email',
    sortOrder: 'asc',
  });

  const [searchQuery] = useDebouncedValue(filters.searchQuery, 200, {
    leading: true,
  });

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<User>>({
    columnAccessor: 'email',
    direction: 'asc',
  });

  useEffect(() => {
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as GetUsersSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: userCount = { count: 0 }, isLoading: isLoadingCount } =
    useCountUsersQuery({ searchQuery, role: filters.role });

  const { data = [], isLoading } = useGetUsersQuery({
    ...filters,
    searchQuery,
  });

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleCreateUserClick = () => {
    openModal({
      title: 'Crea utente',
      children: <CreatePasswordlessUserModal />,
    });
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({ title: 'Utenti' });
  }, [setLayoutProps]);

  return (
    <>
      <Group mb="lg" justify="space-between">
        <Group>
          <QSSelect
            placeholder="Tutti i ruoli"
            data={[
              { label: mapUsersRoles('admin'), value: 'admin' },
              { label: mapUsersRoles('seller'), value: 'seller' },
              { label: mapUsersRoles('client'), value: 'client' },
            ]}
            value={filters.role || null}
            onChange={(value) => {
              setFilters({
                ...filters,
                role: value ? (value as UserRole) : undefined,
              });
            }}
            qsKey="role"
          />
          <SearchBar
            placeholder="Ricerca per nominativo/email"
            value={filters.searchQuery}
            onChange={(value) => setFilters({ ...filters, searchQuery: value })}
          />
        </Group>
        <Button onClick={handleCreateUserClick} leftSection={<IconPlus />}>
          Crea utente
        </Button>
      </Group>
      <DataTable
        striped
        withTableBorder
        fetching={isLoading || isLoadingCount}
        noRecordsText="Nessun utente trovato per i filtri selezionati"
        minHeight={data.length === 0 ? 200 : undefined}
        columns={[
          {
            accessor: 'name',
            title: 'Nominativo',
            render: (record) => (
              <Group gap="xs">
                {!record.active && (
                  <Tooltip label="L'utente è disabilitato">
                    <IconAlertCircle color="red" size={17} />
                  </Tooltip>
                )}
                {record.name}
              </Group>
            ),
          },
          {
            accessor: 'email',
            title: 'Email',
            sortable: true,
          },
          {
            accessor: 'role',
            title: 'Ruolo',
            render: (record) => mapUsersRoles(record.role),
          },
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right',
            width: '0%',
            render: (record) => (
              <Tooltip label="Dettagli utente">
                <ActionIcon
                  variant="subtle"
                  component={Link}
                  to={`/utenti/${record.id}`}
                >
                  <IconInfoSquareRoundedFilled />
                </ActionIcon>
              </Tooltip>
            ),
          },
        ]}
        records={data}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={userCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[10, 20, 50, 100]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
